import { z } from 'zod';

/**
 * Zod schemas for vendor-related entities
 * These provide runtime validation and type inference
 */

// Vendor license schema
const vendorLicenseSchema = z.object({
  id: z.union([z.string(), z.number()]),
  number: z.string(),
  type: z.string(),
  expiryDate: z.string().nullable(),
  file: z.string().nullable().optional(),
});

// Vendor insurance schema
const vendorInsuranceSchema = z.object({
  id: z.union([z.string(), z.number()]),
  type: z.string(),
  provider: z.string(),
  policyNumber: z.string(),
  expiryDate: z.string().nullable(),
  amount: z.string(),
  file: z.string().nullable().optional(),
});

// Vendor coverage area schema
const vendorCoverageAreaSchema = z.object({
  radius: z.number().nonnegative(),
  zipCodes: z.array(z.string()),
});

// Vendor schema (API/Prisma: coerce name, email, w9Status, status)
const w9Enum = ['Submitted', 'Pending', 'Missing'] as const;
const vendorStatusEnum = ['Active', 'Pending', 'Suspended'] as const;

export const vendorSchema = z.object({
  id: z.string(),
  name: z.string().transform(n => (n?.trim() || 'Vendor')),
  email: z.string().email().nullish().or(z.literal('')).transform(v => (v === '' ? undefined : v)),
  phone: z.string().nullish().default(''),
  avatar: z.string().nullish().default(''),
  userId: z.string().nullish(),
  stripeConnectAccountId: z.string().nullish(),
  trade: z.string().nullish().default('General'),
  bio: z.string().nullish(),
  website: z.string().url().nullish().or(z.literal('')),
  hourlyRate: z.number().nonnegative().nullish(),
  services: z.array(z.string()).nullish(),
  address: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zipCode: z.string().nullish(),
  country: z.string().nullish(),
  coverageArea: vendorCoverageAreaSchema.nullish(),
  businessHours: z.record(z.string()).nullish(),
  minimumJob: z.number().nonnegative().nullish(),
  emergencyService: z.boolean().nullish(),
  licenses: z.array(vendorLicenseSchema).nullish(),
  insurance: z.array(vendorInsuranceSchema).nullish(),
  w9Status: z.string().optional().nullable().transform(v => (v && w9Enum.includes(v as (typeof w9Enum)[number]) ? (v as (typeof w9Enum)[number]) : undefined)),
  status: z.string().optional().nullable().transform(v => (v && vendorStatusEnum.includes(v as (typeof vendorStatusEnum)[number]) ? (v as (typeof vendorStatusEnum)[number]) : 'Active')),
  onboardingCompleted: z.boolean().nullish(),
  marketplaceActive: z.boolean().nullish(),
  joinedDate: z.string().nullish(),
  notes: z.string().nullish(),
  xp: z.number().int().nonnegative().nullish().default(0),
  level: z.number().int().nonnegative().nullish().default(1),
  badges: z.array(z.string()).nullish().default([]),
}).passthrough();

export type Vendor = z.infer<typeof vendorSchema>;

// VendorXP schema
export const vendorXPSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  amount: z.number().int().nonnegative(),
  reason: z.string(),
  source: z.enum(['TicketCompletion', 'OnTimeCompletion', 'QualityRating', 'Bonus', 'Manual'] as const),
  ticketId: z.string().optional(),
  createdAt: z.string(),
});

export type VendorXP = z.infer<typeof vendorXPSchema>;

// VendorBadge schema
export const vendorBadgeSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  badgeId: z.string(),
  badgeName: z.string(),
  badgeDescription: z.string(),
  badgeIcon: z.string().optional(),
  earnedAt: z.string(),
  category: z.enum(['Speed', 'Quality', 'Reliability', 'Volume', 'Special'] as const).optional(),
});

export type VendorBadge = z.infer<typeof vendorBadgeSchema>;

// VendorLeaderboardEntry schema
export const vendorLeaderboardEntrySchema = z.object({
  vendorId: z.string(),
  vendorName: z.string(),
  xp: z.number().int().nonnegative(),
  level: z.number().int().nonnegative(),
  completedJobs: z.number().int().nonnegative(),
  rating: z.number().min(0).max(5).nullable(),
  badges: z.number().int().nonnegative(),
  rank: z.number().int().positive(),
});

export type VendorLeaderboardEntry = z.infer<typeof vendorLeaderboardEntrySchema>;

// VendorEarnings schema
export const vendorEarningsSchema = z.object({
  total: z.number().nonnegative(),
  thisMonth: z.number().nonnegative(),
  thisYear: z.number().nonnegative(),
  breakdown: z.array(z.object({
    period: z.string(),
    amount: z.number().nonnegative(),
  })).optional(),
});

export type VendorEarnings = z.infer<typeof vendorEarningsSchema>;

// VendorContract schema
export const vendorContractSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  propertyId: z.string().nullable(),
  type: z.string(),
  status: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  terms: z.string().nullable(),
  documentId: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type VendorContract = z.infer<typeof vendorContractSchema>;

/** GET `/maintenance/vendors/:id/availability` */
export const vendorAvailabilityWindowSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  dayOfWeek: z.number().int().min(0).max(6),
  startTime: z.string(),
  endTime: z.string(),
  effectiveFrom: z.string().nullable().optional(),
  effectiveTo: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type VendorAvailabilityWindowDto = z.infer<typeof vendorAvailabilityWindowSchema>;

/** GET `/maintenance/vendors/:id/reviews` */
export const vendorReviewSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  reviewer: z.string(),
  reviewerId: z.string().optional(),
  rating: z.number(),
  comment: z.string(),
  jobId: z.string().optional(),
  jobTitle: z.string().optional(),
  date: z.string(),
  createdAt: z.string(),
});

export type VendorReviewDto = z.infer<typeof vendorReviewSchema>;

/** GET `/portfolio-items` (vendor portfolio gallery) */
export const vendorPortfolioItemSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  title: z.string(),
  description: z.string(),
  category: z.string(),
  beforePhotos: z.array(z.string()),
  afterPhotos: z.array(z.string()),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
}).passthrough();

export type VendorPortfolioItemDto = z.infer<typeof vendorPortfolioItemSchema>;

/** GET `/vendors/:vendorId/gamification/xp` */
export const vendorXpLedgerEnvelopeSchema = z.object({
  data: z.array(vendorXPSchema),
  totalRows: z.number().int().nonnegative(),
  totalXpAmount: z.number().int(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
});

export type VendorXpLedgerEnvelopeDto = z.infer<typeof vendorXpLedgerEnvelopeSchema>;

/** GET `/maintenance/vendors/:id/screenings` */
export const vendorScreeningSummarySchema = z.object({
  id: z.string(),
  provider: z.string(),
  packageType: z.string(),
  status: z.string(),
  reportId: z.string().nullable(),
  requestedAt: z.string(),
  completedAt: z.string().nullable(),
  error: z.string().nullable(),
  hasArchivedReport: z.boolean(),
});

export type VendorScreeningSummaryDto = z.infer<typeof vendorScreeningSummarySchema>;

/** GET `/maintenance/vendors/:id/tax-summaries` */
export const vendorTaxSummarySchema = z.object({
  id: z.string(),
  taxYear: z.number().int(),
  totalQualifyingPaymentsCents: z.string(),
  excludedPaymentsCents: z.string(),
  formType: z.string().nullable(),
  filedAt: z.string().nullable(),
  stripeLastReportingPushAt: z.string().nullable(),
  stripeLastReportingPushStatus: z.string().nullable(),
});

export type VendorTaxSummaryDto = z.infer<typeof vendorTaxSummarySchema>;
